import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Grid from '@mui/material/Unstable_Grid2';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import TableContainer from '@mui/material/TableContainer';
import { Button, Divider, Link, ListItemText } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { fAPIDate, fDateTime } from 'src/utils/format-time';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import {
  deleteFactoringBatch,
  getFactoringBatchById,
} from 'src/redux/slices/cheque-factoring';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { TableHeadCustom, TableNoData } from 'src/components/table';

import {
  IFactoringBatch,
  getBatchStatusColor,
  FactoringBatchStatus,
} from 'src/types/cheque-factoring';

import BatchChequeTableRow from './batch-cheque-table-row';
import MarkAdvanceReceivedDialog from './mark-advance-received-dialog';

const TABLE_HEAD = [
  { id: 'bank', label: 'Bank / Cheque Number', align: 'left', primary: true },
  { id: 'customer', label: 'Customer', align: 'left', primary: true },
  { id: 'chequeDate', label: 'Cheque Date', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'factoringStatus', label: 'Factoring Status', align: 'left', primary: true },
];

type Props = {
  batch?: IFactoringBatch;
};

export default function BatchDetails({ batch }: Props) {
  const { user: authUser } = useAuthContext();
  const dispatch = useAppDispatcher();
  const navigate = useNavigate();

  const showAdvanceDialog = useBoolean();
  const deleteConfirm = useBoolean();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteBatch = async () => {
    if (!batch) return;

    setIsDeleting(true);

    try {
      await dispatch(deleteFactoringBatch({ batchId: batch._id })).unwrap();

      enqueueSnackbar('Batch deleted — its cheques are available for factoring again');
      navigate(paths.dashboard.chequeFactoring.batches.root);
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to delete batch', { variant: 'error' });
      }
    } finally {
      setIsDeleting(false);
      deleteConfirm.onFalse();
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle1">Batch Information</Typography>
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary="Batch Date"
                  secondary={fAPIDate(batch?.batchDate)}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Label
                      variant="soft"
                      color={getBatchStatusColor(batch?.displayStatus ?? batch?.status)}
                    >
                      {batch?.displayStatus ?? batch?.status}
                    </Label>
                  }
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary="Total Amount"
                  secondary={fCurrencyRupees(batch?.totalAmountInCents ?? 0)}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary="Advance (80%)"
                  secondary={fCurrencyRupees(batch?.advanceAmountInCents ?? 0)}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>

              {batch?.advanceReceivedDate && (
                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Advance Received Date"
                    secondary={fDateTime(batch.advanceReceivedDate)}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                  />
                </Grid>
              )}

              <Grid xs={12} md={3}>
                <ListItemText
                  primary="Cheques"
                  secondary={batch?.cheques.length ?? 0}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>

              {batch?.createdBy && (
                <Grid xs={12} md={3}>
                  <ListItemText
                    primary={
                      <Link
                        component={RouterLink}
                        href={paths.dashboard.user.view(batch.createdBy._id ?? '')}
                      >
                        {`${batch.createdBy.firstName} ${batch.createdBy.lastName}`}
                      </Link>
                    }
                    secondary="Created By"
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2.5, borderStyle: 'dashed' }} />

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Cheques in this batch
            </Typography>

            <TableContainer sx={{ position: 'relative', overflow: 'hidden' }}>
              <Scrollbar>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHeadCustom headLabel={TABLE_HEAD} />

                  <TableBody>
                    {batch?.cheques.map((cheque) => (
                      <BatchChequeTableRow key={cheque._id} row={cheque} />
                    ))}

                    {batch && batch.cheques.length === 0 && (
                      <TableNoData message="No cheques in this batch" notFound />
                    )}
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
              <Link component={RouterLink} href={paths.dashboard.chequeFactoring.batches.root}>
                <Button variant="outlined" color="inherit">
                  Back
                </Button>
              </Link>

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.DELETE_FACTORING_BATCH,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                batch?.status === FactoringBatchStatus.SUBMITTED && (
                  <Button
                    variant="outlined"
                    color="error"
                    type="button"
                    onClick={() => deleteConfirm.onTrue()}
                  >
                    Delete Batch
                  </Button>
                )}

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.UPDATE_FACTORING_BATCH,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                batch?.status === FactoringBatchStatus.SUBMITTED && (
                  <Button
                    variant="contained"
                    type="button"
                    onClick={() => showAdvanceDialog.onTrue()}
                  >
                    Mark Advance Received
                  </Button>
                )}
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {batch && showAdvanceDialog.value && (
        <MarkAdvanceReceivedDialog
          batch={batch}
          isOpen={showAdvanceDialog.value}
          onClose={() => showAdvanceDialog.onFalse()}
          onSuccess={() => dispatch(getFactoringBatchById({ batchId: batch._id }))}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm.value}
        onClose={deleteConfirm.onFalse}
        title="Delete Batch"
        content="Are you sure you want to delete this batch? Its cheques will become unfactored and available for a future batch. This action cannot be undone."
        action={
          <LoadingButton
            variant="contained"
            color="error"
            loading={isDeleting}
            onClick={handleDeleteBatch}
          >
            Delete
          </LoadingButton>
        }
      />
    </>
  );
}
